import React, { useEffect, useMemo, useRef } from 'react'
import {
  OeTestRunStatus,
  OeTestStatus,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { OeControlTestResult } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { SOURCE_RESULT } from '@trustero/trustero-api-web/lib/source/source_pb'
import { faHourglass2 } from '@fortawesome/free-regular-svg-icons'
import GradientSpinner from 'src/Throbber/GradientSpinner'
import { SectionTitle } from 'src/components/Reusable/Text/Text.styles'
import { useTypewriter } from 'src/Utils/globalHooks'
import { DropdownSVG } from 'src/components/Icons/Basic'
import {
  Tooltip,
  TooltipOverlayType,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { ControlCheckTestProcedure } from 'src/pages/TrusteroIntelligence/artifacts/AuditScan/AuditScanArtifact.components'
import { getOESourceResultIcon } from 'src/pages/TrusteroIntelligence/artifacts/AuditScan/AuditScanArtifact.helpers'
import { useOeControlTestResults } from 'src/pages/AuditBot/AuditBot.hooks'
import {
  cleanTestProcedureDescription,
  getControlTestName,
} from '../../accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import {
  CONTROL_TEST_NAMES,
  PASSED_CONTROL_CHECK_TEXT,
} from '../../accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import {
  ControlCheckDescriptionRow,
  ControlCheckEroText,
  ControlCheckMarkup,
  ControlCheckResultContainer,
  ControlCheckSourcesContainer,
  ControlCheckSummaryTitle,
  ControlCheckTestAccordionBody,
  ControlCheckTestAccordionHeader,
  ControlCheckTestAccordionItem,
  ControlCheckTestProcedureBody,
  ControlCheckTestProcedureContainer,
  ControlCheckTestProcedureDescription,
  ControlCheckTestProcedureIcon,
  ControlCheckTestProcedureNumber,
} from '../../accordion/subsection/ControlChecks/AuditBotControlChecks.styles'
import {
  DocumentsReadyCheckContainer,
  DocumentsReadyCheckHeader,
} from '../../accordion/subsection/AuditBotSubSectionAccordion.styles'
import {
  OeLiveLoadingRow,
  OeLiveNarration,
  OeTooltipBody,
  StyledOeQueuedIcon,
} from './OeLiveStatus.styles'
import {
  OE_QUEUED_NARRATION,
  getLatestNarration,
  getProcedureStatusLabel,
  isControlQueued,
  isTerminalStatus,
} from './OeLiveStatus.helpers'

// A terminal status's outcome -> the same SOURCE_RESULT icon the finished
// procedure uses, so the spinner->icon flip matches the eventual result row.
const terminalSourceResult = (status: OeTestStatus): SOURCE_RESULT => {
  if (status.getRunStatus() === OeTestRunStatus.OE_TEST_ERROR) {
    return SOURCE_RESULT.UNKNOWN
  }
  switch (status.getOutcome().toUpperCase()) {
    case 'PASS':
      return SOURCE_RESULT.PASS
    case 'FAIL':
      return SOURCE_RESULT.FAIL
    default:
      return SOURCE_RESULT.UNKNOWN
  }
}

// The queued glyph (a muted hourglass) for a control whose OE tests haven't
// started.
export const OeQueuedIcon = (): JSX.Element => (
  <StyledOeQueuedIcon icon={faHourglass2} />
)

// The running glyph: TI's blue-purple gradient spinner, sized to match the small
// header/procedure icons, shown while a control is actively testing.
export const OeRunningSpinner = (): JSX.Element => (
  <GradientSpinner size={16} thickness={2} durationMs={1000} />
)

// The control's latest live status, shown in the accordion header to the right
// of the control name. Mirrors the body's per-procedure narration styling
// (OeLiveNarration), truncated to keep the header tidy — with a hover tooltip
// carrying the full text. Live updates type in; the static "queued" placeholder
// shows instantly (not a status worth animating).
export const OeHeaderNarration = ({
  liveStatuses,
}: {
  liveStatuses: OeTestStatus[]
}): JSX.Element | null => {
  const label = getLatestNarration(liveStatuses)
  const isQueued = label === OE_QUEUED_NARRATION
  const typed = useTypewriter({
    text: label,
    interval: 10,
    shouldType: !isQueued,
  })
  if (!label) return null
  const controlId = liveStatuses[0]?.getControlId() ?? ''
  return (
    <Tooltip
      id={`oe-header-narration-${controlId}`}
      overlayType={TooltipOverlayType.popover}
      hideArrow
      placement="top"
      tooltipBody={<OeTooltipBody>{label}</OeTooltipBody>}
    >
      <OeLiveNarration $truncate>{isQueued ? label : typed}</OeLiveNarration>
    </Tooltip>
  )
}

// One live OE test-procedure row while it's still processing (pending ->
// running -> terminal-awaiting-result). Uses the SAME grid primitives as the
// finished ControlCheckTestProcedure so the two align, with the status shown
// UNDER the description (in the body cell) as typed narration (TI's typewriter)
// — no dots, no pills.
export const OeLiveProcedureRow = ({
  status,
  orderNumber,
  total,
}: {
  status: OeTestStatus
  orderNumber: number
  total: number
}): JSX.Element => {
  const description =
    cleanTestProcedureDescription(status.getTestString()) ||
    `Test procedure ${status.getTestIdx() + 1}`
  const showNumber = total > 1
  const isTerminal = isTerminalStatus(status)

  const icon = isTerminal ? (
    getOESourceResultIcon(terminalSourceResult(status))
  ) : (
    <OeRunningSpinner />
  )

  // TI speed (10ms/char) for status lines; re-types when the narration changes.
  const typedLabel = useTypewriter({
    text: getProcedureStatusLabel(status),
    interval: 10,
  })

  return (
    <ControlCheckTestProcedureContainer $isFullWidth>
      <ControlCheckTestProcedureIcon>{icon}</ControlCheckTestProcedureIcon>
      {showNumber && (
        <ControlCheckTestProcedureNumber>
          {orderNumber}.
        </ControlCheckTestProcedureNumber>
      )}
      <ControlCheckTestProcedureDescription $showNumber={showNumber}>
        <ControlCheckMarkup markdown={description} />
      </ControlCheckTestProcedureDescription>
      <ControlCheckTestProcedureBody $showNumber={showNumber}>
        <OeLiveNarration>{typedLabel}</OeLiveNarration>
      </ControlCheckTestProcedureBody>
    </ControlCheckTestProcedureContainer>
  )
}

// The live "TEST DETAILS" body for an in-progress control: one row per OE test
// procedure. A procedure that has finished (terminal status + its result has
// been fetched) renders through the shared ControlCheckTestProcedure with
// typeReasoning, so its answer types in as soon as it lands — independently of
// its siblings. Still-running procedures render as OeLiveProcedureRow.
//
// The finished results are fetched lazily (only when isExpanded) and polled
// while at least one procedure is terminal, so a collapsed control fetches
// nothing.
export const OeLiveProcedureList = ({
  auditRunId,
  liveStatuses,
  isExpanded,
}: {
  auditRunId: string
  liveStatuses: OeTestStatus[]
  isExpanded: boolean
}): JSX.Element | null => {
  const controlId = liveStatuses[0]?.getControlId() ?? ''
  // Real procedure rows carry a test string; a synthesized PENDING placeholder
  // for a still-queued control does not — drop those so a fully-queued control
  // shows the "preparing" placeholder rather than a fake "Test procedure 1" row.
  const procedureStatuses = liveStatuses.filter((s) => s.getTestString().trim())
  const anyTerminal = procedureStatuses.some(isTerminalStatus)
  const { data } = useOeControlTestResults(
    auditRunId,
    controlId,
    isExpanded && anyTerminal,
  )

  const resultByIdx = useMemo(() => {
    const map = new Map<number, OeControlTestResult>()
    data?.getResultsList().forEach((r) => {
      const res = r.getResult()
      if (res) map.set(r.getTestIdx(), res)
    })
    return map
  }, [data])

  // Procedures already finished when the accordion was opened — captured from
  // liveStatuses (available immediately, before the results fetch). Only
  // procedures that finish AFTER the open animate; re-opening later re-baselines,
  // so a procedure that finished a while ago never re-types.
  const finishedAtOpenRef = useRef<Set<number> | null>(null)
  useEffect(() => {
    if (!isExpanded) {
      finishedAtOpenRef.current = null
    } else if (finishedAtOpenRef.current === null) {
      finishedAtOpenRef.current = new Set(
        liveStatuses.filter(isTerminalStatus).map((s) => s.getTestIdx()),
      )
    }
  }, [isExpanded, liveStatuses])

  // Render only while expanded: react-bootstrap keeps a collapsed accordion's
  // body mounted, so if these rows mounted while hidden their typewriters would
  // finish before the user opened the accordion (static text on expand). Mounting
  // on expand makes the narration/answers animate when actually visible.
  if (!isExpanded) {
    return null
  }

  const finishedAtOpen = finishedAtOpenRef.current

  const total = procedureStatuses.length
  return (
    // DocumentsReadyCheckContainer gives the 0.875rem font the completed OE
    // result inherits, so live procedure numbers / "Evidence and Sources" match.
    // Structure mirrors the completed ControlCheckResult body (question →
    // SUMMARY → TEST DETAILS) so nothing shifts when the results land.
    <DocumentsReadyCheckContainer>
      <ControlCheckResultContainer>
        <ControlCheckDescriptionRow>
          {PASSED_CONTROL_CHECK_TEXT[CONTROL_TEST_NAMES.SPOT_CHECK]}
        </ControlCheckDescriptionRow>
        <ControlCheckSummaryTitle>SUMMARY</ControlCheckSummaryTitle>
        {/* ControlCheckEroText carries grid-column: 2 / -1 so the placeholder
            sits in the content column, not the 16px icon column. */}
        <ControlCheckEroText $isItalic>
          <OeLiveNarration>
            The overall summary is generated once every procedure finishes.
          </OeLiveNarration>
        </ControlCheckEroText>
      </ControlCheckResultContainer>
      <ControlCheckSourcesContainer $isFullWidth>
        <SectionTitle $size={14} $mb={8}>
          TEST DETAILS
        </SectionTitle>
        {total ? (
          procedureStatuses.map((status) => {
            const idx = status.getTestIdx()
            const result = resultByIdx.get(idx)
            // Animate only if this procedure finished after the accordion opened.
            const justFinalized =
              finishedAtOpen !== null && !finishedAtOpen.has(idx)
            return isTerminalStatus(status) && result ? (
              <ControlCheckTestProcedure
                key={`${controlId}-${idx}`}
                orderNumber={idx + 1}
                testProcedure={result}
                total={total}
                typeReasoning={justFinalized}
              />
            ) : (
              <OeLiveProcedureRow
                key={`${controlId}-${idx}`}
                status={status}
                orderNumber={idx + 1}
                total={total}
              />
            )
          })
        ) : (
          // The control's tests are enqueued and haven't started — the OE status
          // poll only surfaces per-procedure detail once a control starts
          // running, so show a queued placeholder rather than an empty section.
          <OeLiveLoadingRow>
            <OeLiveNarration>{OE_QUEUED_NARRATION}</OeLiveNarration>
          </OeLiveLoadingRow>
        )}
      </ControlCheckSourcesContainer>
    </DocumentsReadyCheckContainer>
  )
}

// The live OE test accordion item for an in-progress control — the SAME
// ControlCheckTestAccordionItem shape the completed "Operating Effectiveness
// Test Result" uses (spinner header + chevron), so when the control finishes and
// its completed row renders under the same stable eventKey, an expanded live
// accordion stays expanded. Its body holds the per-procedure live rows.
export const OeLiveTestAccordionItem = ({
  eventKey,
  auditRunId,
  liveStatuses,
  isExpanded,
  handleClick,
}: {
  eventKey: string
  auditRunId: string
  liveStatuses: OeTestStatus[]
  isExpanded: boolean
  handleClick: (e: React.MouseEvent, eventKey: string) => void
}): JSX.Element => (
  <ControlCheckTestAccordionItem eventKey={eventKey} $hoverable>
    <ControlCheckTestAccordionHeader
      $isActive={isExpanded}
      onClick={(e: React.MouseEvent) => handleClick(e, eventKey)}
    >
      <DocumentsReadyCheckHeader>
        {isControlQueued(liveStatuses) ? (
          <OeQueuedIcon />
        ) : (
          <OeRunningSpinner />
        )}
        {`${getControlTestName(CONTROL_TEST_NAMES.SPOT_CHECK)} Result`}
        <DropdownSVG className="control-check-chevron" />
      </DocumentsReadyCheckHeader>
    </ControlCheckTestAccordionHeader>
    <ControlCheckTestAccordionBody>
      <OeLiveProcedureList
        auditRunId={auditRunId}
        liveStatuses={liveStatuses}
        isExpanded={isExpanded}
      />
    </ControlCheckTestAccordionBody>
  </ControlCheckTestAccordionItem>
)
